/**
 * EG Search (Meilisearch) – analytics hooks.
 *
 * Tracks search clicks + conversions and stores lightweight attribution in `sessionStorage`
 * so orders can be attributed later.
 */
window.EGSearchAnalytics = (function () {
  'use strict';

  const config = window.EGSearchConfig;
  const SESSION_KEY = 'eg_last_search_query';
  const SESSION_SOURCE_KEY = 'eg_traffic_source';
  const SESSION_SEARCH_ID_KEY = 'eg_search_id';
  const SESSION_POSITION_KEY = 'eg_search_position';
  const SESSION_ORIGIN_KEY = 'eg_search_origin';
  const SESSION_EXPIRES_KEY = 'eg_search_expires';
  const SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutes

  // Attribution sources
  const SOURCES = {
    SEARCH: 'search',
    AUTOCOMPLETE: 'autocomplete',
    COLLECTION: 'collection',
    DIRECT: 'direct'
  };

  function normalizeSource(raw) {
    const s = String(raw || '').trim().toLowerCase();
    if (!s) return '';
    if (s === 'search') return SOURCES.SEARCH;
    if (s === 'autocomplete') return SOURCES.AUTOCOMPLETE;
    if (s === 'collection') return SOURCES.COLLECTION;
    if (s === 'direct') return SOURCES.DIRECT;

    // Common aliases (WordPress/blog → shop)
    if (s === 'wordpress' || s === 'wp') return SOURCES.SEARCH;
    if (s === 'blog' || s === 'blog_search') return SOURCES.SEARCH;
    if (s === 'blog_autocomplete') return SOURCES.AUTOCOMPLETE;

    if (s.includes('collect')) return SOURCES.COLLECTION;
    if (s.includes('auto')) return SOURCES.AUTOCOMPLETE;
    if (s.includes('search')) return SOURCES.SEARCH;
    return s;
  }

  function normalizeDomain(hostOrUrl) {
    const raw = String(hostOrUrl || '').trim().toLowerCase();
    if (!raw) return '';
    try {
      const u = raw.includes('://') ? new URL(raw) : null;
      const host = u ? u.hostname : raw.split('/')[0];
      return host.replace(/^www\./, '');
    } catch (e) {
      return raw.replace(/^www\./, '').split('/')[0];
    }
  }

  // Persist cart attributes so orders can be attributed (best-effort, keepalive=true).
  let lastPersistKey = '';
  function schedulePersistAttributionToCart() {
    try {
      const attr = getAttribution();
      const key = `${attr.source}::${attr.query}::${attr.searchId}::${attr.origin}`;
      if (key === lastPersistKey) return;
      lastPersistKey = key;
      persistAttributionToCart(attr);
    } catch (e) { /* ignore */ }
  }

  // Save the last browse/search context for attribution.
  function setAttribution(query, source, origin) {
    if (!source) return;
    try {
      sessionStorage.setItem(SESSION_KEY, (query || '').trim().toLowerCase());
      sessionStorage.setItem(SESSION_SOURCE_KEY, normalizeSource(source) || source);
      if (origin) {
        sessionStorage.setItem(SESSION_ORIGIN_KEY, origin);
      } else if (!sessionStorage.getItem(SESSION_ORIGIN_KEY)) {
        sessionStorage.setItem(SESSION_ORIGIN_KEY, window.location.hostname);
      }
      sessionStorage.setItem(SESSION_EXPIRES_KEY, String(Date.now() + SESSION_TTL_MS));
      schedulePersistAttributionToCart();
    } catch (e) { /* ignore */ }
  }

  function setAttributionMeta({ searchId, position }) {
    try {
      if (searchId) sessionStorage.setItem(SESSION_SEARCH_ID_KEY, String(searchId));
      if (position) sessionStorage.setItem(SESSION_POSITION_KEY, String(position));
      schedulePersistAttributionToCart();
    } catch (e) { /* ignore */ }
  }

  // Read stored attribution info.
  function getAttribution() {
    try {
      const expires = parseInt(sessionStorage.getItem(SESSION_EXPIRES_KEY) || '0', 10);
      if (Date.now() > expires) {
        sessionStorage.removeItem(SESSION_KEY);
        sessionStorage.removeItem(SESSION_SOURCE_KEY);
        sessionStorage.removeItem(SESSION_SEARCH_ID_KEY);
        sessionStorage.removeItem(SESSION_ORIGIN_KEY);
        sessionStorage.removeItem(SESSION_POSITION_KEY);
        sessionStorage.removeItem(SESSION_EXPIRES_KEY);
        return { query: '', source: SOURCES.DIRECT, searchId: '', position: 0, origin: '' };
      }
      return {
        query: sessionStorage.getItem(SESSION_KEY) || '',
        source: sessionStorage.getItem(SESSION_SOURCE_KEY) || SOURCES.DIRECT,
        searchId: sessionStorage.getItem(SESSION_SEARCH_ID_KEY) || '',
        position: parseInt(sessionStorage.getItem(SESSION_POSITION_KEY) || '0', 10) || 0,
        origin: sessionStorage.getItem(SESSION_ORIGIN_KEY) || window.location.hostname,
      };
    } catch (e) {
      return { query: '', source: SOURCES.DIRECT, searchId: '', position: 0, origin: '' };
    }
  }

  // Legacy compatibility
  function setLastSearchQuery(query) {
    setAttribution(query, SOURCES.SEARCH);
  }

  function getLastSearchQuery() {
    return getAttribution().query;
  }

  // Track a product click from search.
  function trackClick(query, productId, position, productTitle, indexName = 'products', searchType = 'search', searchId = '') {
    if (!query || !productId) return;
    const src = (String(searchType || '').toLowerCase() === SOURCES.AUTOCOMPLETE) ? SOURCES.AUTOCOMPLETE : SOURCES.SEARCH;
    setAttribution(query, src);
    setAttributionMeta({ searchId, position });
    try {
      fetch(`${config.API_BASE}/analytics/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          product_id: productId,
          product_title: productTitle,
          position: position || 0,
          index_name: indexName,
          search_type: searchType,
          search_id: searchId || null,
          origin_domain: getAttribution().origin || window.location.hostname,
        }),
        keepalive: true
      }).catch(() => { }); // Silent fail
    } catch (e) { /* ignore */ }
  }

  // Track conversion events (add_to_cart, purchase, etc.).
  function trackConversion(query, productId, productTitle, eventType = 'add_to_cart', indexName = 'products', searchType = 'search', searchId = '', position = 0) {
    // Use stored attribution if not provided
    const attribution = getAttribution();
    const effectiveQuery = query || attribution.query;
    const effectiveSource = searchType || attribution.source;
    const effectiveSearchId = searchId || attribution.searchId;
    const effectivePosition = position || attribution.position || 0;

    // console.log('[EGSearchAnalytics] trackConversion called:', { query, productId, productTitle, eventType, effectiveSource, effectiveSearchId, effectivePosition });

    if (!productId) {
      console.warn('[EGSearchAnalytics] trackConversion: No productId provided');
      return;
    }

    // Keep attribution "warm" so the /cart/add persistence hook can write the right cart attributes.
    // This covers add-to-cart flows where the user never clicked a product card first.
    try {
      if (effectiveSource) setAttribution(effectiveQuery, effectiveSource);
      setAttributionMeta({ searchId: effectiveSearchId, position: effectivePosition });
    } catch (e) { /* ignore */ }

    try {
      // console.log('[EGSearchAnalytics] Sending conversion to:', `${config.API_BASE}/analytics/conversion`);
      fetch(`${config.API_BASE}/analytics/conversion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: effectiveQuery,
          product_id: productId,
          product_title: productTitle,
          event_type: eventType,
          index_name: indexName,
          search_type: effectiveSource, // Use source as search_type for attribution
          search_id: effectiveSearchId || null,
          position: effectivePosition || 0,
          origin_domain: attribution.origin || window.location.hostname,
        }),
        keepalive: true
      })
        .then(res => {
          if (res.ok) {
            // console.log('[EGSearchAnalytics] Conversion tracked successfully:', res.status);
          } else {
            console.warn('[EGSearchAnalytics] Conversion tracking failed:', res.status);
          }
        })
        .catch((err) => {
          console.error('[EGSearchAnalytics] Conversion tracking error:', err);
        });
    } catch (e) {
      console.error('[EGSearchAnalytics] Exception in trackConversion:', e);
    }
  }


  async function persistAttributionToCart({ query, source, searchId, origin }) {
    try {
      const q = String(query || '').trim().toLowerCase();
      const s = String(source || SOURCES.DIRECT);
      const sid = String(searchId || '');
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            eg_source: s,
            eg_query: q,
            eg_search_id: sid,
            eg_origin_domain: origin || window.location.hostname,
            eg_ts: String(Date.now()),
          }
        }),
        keepalive: true
      }).catch(() => { });
    } catch (e) { /* ignore */ }
  }

  // Track collection page events
  function trackCollection(eventType, collectionHandle, collectionTitle, productId = null, productTitle = null, position = 0) {
    // When clicking a product in a collection, store collection as source
    if (eventType === 'click' && productId) {
      setAttribution(collectionHandle, SOURCES.COLLECTION);
    }

    try {
      fetch(`${config.API_BASE}/analytics/collection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_type: eventType,
          collection_handle: collectionHandle,
          collection_title: collectionTitle,
          product_id: productId,
          product_title: productTitle,
          position: position,
          origin_domain: window.location.hostname,
        }),
        keepalive: true
      }).catch(() => { }); // Silent fail
    } catch (e) { /* ignore */ }
  }

  // Initialize click tracking delegation
  function init(getCurrentQuery) {
    // Check for external attribution params (e.g. from WordPress or other referrers)
    try {
      const params = new URLSearchParams(window.location.search);
      const extSource = params.get('eg_source');
      if (extSource) {
        const extQuery = params.get('eg_query') || '';
        const extSearchId = params.get('eg_search_id') || '';
        const rawExtSource = String(extSource || '').trim().toLowerCase();
        const blogSources = new Set(['wordpress', 'wp', 'blog', 'blog_search', 'blog_autocomplete']);

        let extOrigin = params.get('eg_origin_domain') || '';
        if (!extOrigin) {
          const refHost = normalizeDomain(document.referrer || '');
          const curHost = normalizeDomain(window.location.hostname || '');
          if (refHost && curHost && refHost !== curHost) {
            extOrigin = refHost;
          } else if (blogSources.has(rawExtSource)) {
            extOrigin = 'epicgardening.com';
          }
        }
        extOrigin = normalizeDomain(extOrigin);
        setAttribution(extQuery, extSource, extOrigin);
        setAttributionMeta({ searchId: extSearchId });
      }
    } catch (e) { }

    // If add-to-cart uses a normal HTML form submit (no fetch/XHR), persist attribution anyway.
    document.addEventListener('submit', (e) => {
      try {
        const form = e.target;
        if (!form || !form.getAttribute) return;
        const action = String(form.getAttribute('action') || '');
        if (!action.includes('/cart/add')) return;
        persistAttributionToCart(getAttribution());
      } catch (err) { /* ignore */ }
    }, true);

    document.addEventListener('click', (e) => {
      const el = e.target.closest('[data-eg-track-click="1"]');
      if (!el) return;

      const query = el.getAttribute('data-eg-query') || (getCurrentQuery ? getCurrentQuery() : '') || '';
      const productId = el.getAttribute('data-eg-product-id') || '';
      const productTitle = el.getAttribute('data-eg-product-title') || null;
      const position = parseInt(el.getAttribute('data-eg-position') || '0', 10) || 0;
      const indexName = el.getAttribute('data-eg-index-name') || 'products';
      const searchType = el.getAttribute('data-eg-search-type') || 'search';
      const searchId = el.getAttribute('data-eg-search-id') || '';

      trackClick(query, productId, position, productTitle, indexName, searchType, searchId);
    });

    // Initialize collection page tracking
    initCollectionTracking();

    // Best-effort: persist attribution to cart after ANY add-to-cart event (so the purchase webhook can attribute).
    // This does not depend on line-item properties, so it works across theme components/apps.
    try {
      const _fetch = window.fetch;
      window.fetch = function () {
        const p = _fetch.apply(this, arguments);
        try {
          const url = arguments && arguments[0] ? String(arguments[0]) : '';
          if (url.includes('/cart/add')) {
            // Persist current attribution to cart attributes (note_attributes on the order)
            p.then(() => persistAttributionToCart(getAttribution())).catch(() => { });
          }
        } catch (e) { }
        return p;
      };
    } catch (e) { /* ignore */ }
  }

  // Auto-track collection page views and product clicks
  function initCollectionTracking() {
    // Check if we're on a collection page
    const collectionHandle = window.location.pathname.match(/\/collections\/([^\/\?]+)/)?.[1];
    if (!collectionHandle || collectionHandle === 'all') return;

    // Get collection title from page
    const collectionTitle = document.querySelector('.collection-hero__title, .page-title, h1')?.textContent?.trim() || collectionHandle;

    // Track collection view
    trackCollection('view', collectionHandle, collectionTitle);

    // Track product clicks within collection (PDP = click through to product page)
    document.addEventListener('click', (e) => {
      const productLink = e.target.closest(
        '.product-card a, .product-item a, [data-product-card] a, .card-wrapper a, .product-card-wrapper a'
      );
      if (!productLink) return;
      if (!productLink.href || !productLink.href.includes('/products/')) return;

      const card = productLink.closest(
        '.product-card, .product-item, [data-product-card], .card-wrapper, .product-card-wrapper'
      );
      if (!card) return;

      const productId =
        card.dataset.productId ||
        card.querySelector('[data-product-id]')?.dataset?.productId ||
        productLink.href?.match(/\/products\/([^\/\?]+)/)?.[1] ||
        '';
      const productTitle =
        card.querySelector(
          '.product-card__title, .product-item__title, [data-product-title], .card__heading'
        )?.textContent?.trim() ||
        '';
      const cards = Array.from(
        document.querySelectorAll(
          '.product-card, .product-item, [data-product-card], .card-wrapper, .product-card-wrapper'
        )
      );
      const position = cards.indexOf(card) + 1;

      trackCollection('click', collectionHandle, collectionTitle, productId, productTitle, position);
    });

    // Track Quick Add (Add to Cart) submissions on collection pages
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-type="add-to-cart-form"]');
      if (!form) return;

      const card = form.closest('.card-wrapper, .product-card, .product-item, [data-product-card]');
      if (!card) return;

      const productId = card.dataset.productId || card.querySelector('[data-product-id]')?.dataset?.productId || '';
      const productTitle = card.querySelector('.card__heading, .product-card__title, .product-item__title')?.textContent?.trim() || '';
      const cards = Array.from(document.querySelectorAll('.card-wrapper, .product-card, .product-item, [data-product-card]'));
      const position = cards.indexOf(card) + 1;

      // Track as conversion with collection attribution
      trackConversion(
        collectionHandle,
        productId,
        productTitle,
        'add_to_cart',
        'products',
        SOURCES.COLLECTION,
        '',
        position
      );

      // Set attribution for this add-to-cart action
      setAttribution(collectionHandle, SOURCES.COLLECTION);
      setAttributionMeta({ position });
    });
  }

  return {
    trackClick,
    trackConversion,
    trackCollection,
    setLastSearchQuery,
    getLastSearchQuery,
    setAttribution,
    getAttribution,
    SOURCES,
    init,
    initCollectionTracking
  };
})();

